import { useCallback, useContext, useMemo, useState } from 'react';
import useResizeLayout from '@Hooks/useResizeLayout';

import { Divider, Flex, Form, Modal, Typography } from 'antd';
import { MButton, MInput, MRadio } from '@Components/Base';
import MUpload from '@Components/Base/Upload/Upload';

import { ViewResolver } from '@Components/ViewResolver';
import { MobileDrawer } from '@Components/index';

import { useFormUploadMutation } from '@Store/api/dmsApiSlice';
import { FileUpload } from '@Types/index';
import { DocumentFormUploadResponse } from '@Types/api/form';
import { fileUploadActions } from '@Store/slices/fileUploadSlice';
import { generateFilepath } from '@Utils/generateFilepath';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@Hooks/index';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import { currentFileUploadSelector } from '@Store/selectors/fileUploadSelector';
import { getModalRecordKey } from '@Store/selectors/modalSelector';
import { RcFile } from 'antd/es/upload';
import { FormikHelpers, useFormik } from 'formik';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import {
  getCompaniesDictSelector,
  getTenderExtensionTypeDict,
} from '@Store/selectors/dictionarySelector';
import { NotificationContext } from '@Context/NotificationContext';
import { getFormUploadFileMetaData } from '../../MaterialRequisition/form/utils/document';
import { useUpdateTenderExtensionsMutation } from '../../TenderRequest/store/api/TenderRequestApiSlice';
import {
  mapTenderExtensionUpdateForm,
  TenderExtensionForm,
} from '../../TenderRequest/form/utils/tenderExtensionUpdate';

import styles from './TenderExtentionModal.module.css';

interface TenderExtentionModalProps {
  isOpen: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

const initialValues: TenderExtensionForm = {
  extensionType: 'Submittal Reference',
  extensionId: '',
  extensionAttachment: null,
};

const TenderExtentionModal: React.FC<TenderExtentionModalProps> = ({
  isOpen,
  onSuccess,
  onCancel,
}) => {
  const tenderId = useAppSelector(getModalRecordKey);
  const currentUser = useAppSelector(currentUserSelector);
  const companies = useAppSelector(getCompaniesDictSelector);
  const currentFileUpload = useAppSelector(currentFileUploadSelector);
  const tenderExtensionType = useAppSelector(getTenderExtensionTypeDict);
  const [formDocumentId, setFormDocumentId] = useState<string>();

  const { errorNotification } = useContext(NotificationContext);

  const dispatch = useDispatch();

  const [formUpload] = useFormUploadMutation();
  const { isMobile } = useResizeLayout();

  const [updateTenderExtensions] = useUpdateTenderExtensionsMutation();

  const compId = useMemo(() => {
    return (
      companies.find((cmp) => cmp.compCode === currentUser.companyCode)
        ?.compId || null
    );
  }, [companies, currentUser.companyCode]);

  const uploadFile = useCallback(
    (
      reqFileId: string,
      currentFile: FileUpload
    ): Promise<DocumentFormUploadResponse> => {
      const { formData, filepath } = currentFile;

      const body = getFormUploadFileMetaData(reqFileId, filepath);

      return formUpload({
        document: JSON.stringify(body),
        body: formData,
      }).unwrap();
    },
    [formUpload]
  );

  const handleSaveDocument = useCallback(
    async (
      values: TenderExtensionForm,
      formikHelpers: FormikHelpers<TenderExtensionForm>
    ) => {
      const { extensionTypeId, extensionId } = mapTenderExtensionUpdateForm(
        values,
        tenderExtensionType
      );

      if (
        currentFileUpload.filepath &&
        compId &&
        extensionTypeId &&
        extensionId &&
        tenderId
      ) {
        const fileMetaData = await uploadFile(
          String(tenderId),
          currentFileUpload
        )
          .then((meta) => {
            if (meta.documentId) {
              setFormDocumentId(meta.documentId);
            }
            return meta;
          })
          .catch((err) => {
            if (err.data === 'Failed to upload image') {
              // use already uploaded documentId from the state
              return { documentId: formDocumentId };
            }
            return err;
          });

        const body = {
          compId,
          tenderId: Number(tenderId),
          extensionTypeId,
          extensionId,
          extensionAttachment: fileMetaData.documentId,
        };

        updateTenderExtensions(body)
          .unwrap()
          .then(() => {
            onSuccess();
            dispatch(fileUploadActions.setFile(null));
            formikHelpers.resetForm();
          })
          .catch((error) => {
            errorNotification(error.data);
          });
      } else {
        errorNotification(
          'Please fill in the text field and attach a document.'
        );
      }
    },
    [
      compId,
      currentFileUpload,
      dispatch,
      errorNotification,
      formDocumentId,
      onSuccess,
      tenderExtensionType,
      tenderId,
      updateTenderExtensions,
      uploadFile,
    ]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSaveDocument,
  });

  const handleFileRequest = useCallback(
    (options: RcCustomRequestOptions): void => {
      const { file, onSuccess: onFileRequestSuccess, onError } = options;
      const cb = (): void => {};
      const successCb = onFileRequestSuccess || cb;
      const errorCb = onError || cb;
      const formData = new FormData();
      formData.append('file', file);

      if (file) {
        const filepath = generateFilepath(
          'TR',
          (file as RcFile).name,
          currentUser?.empNumber
        );
        dispatch(
          fileUploadActions.setFile({
            uploadedFile: file as RcFile,
            formData,
            filepath,
          })
        );
        successCb('Ok');
      } else {
        errorCb(new Error('file not uploaded'));
      }
    },
    [currentUser?.empNumber, dispatch]
  );

  const handleFileRemove = useCallback((): void => {
    dispatch(fileUploadActions.setFile(null));
  }, [dispatch]);

  const customButtons = [
    <MButton key="back" type="tertiary" onClick={onCancel}>
      Cancel
    </MButton>,
    <MButton
      key="submit"
      type="primary"
      htmlType="submit"
      onClick={formik.submitForm}
    >
      Save
    </MButton>,
  ];

  const labelMap: Record<string, string> = {
    'Submittal Reference': 'Tender ID',
    'Continuous Order': 'LPO No',
    LOI: 'LOI',
  };

  const tenderIdLabel = labelMap[formik.values.extensionType];

  const content = useMemo(
    () => (
      <Flex className={styles.main} vertical gap={16}>
        {!isMobile && <Divider className={styles.divider} />}
        <Flex vertical gap={isMobile ? 16 : 6}>
          <Typography className={styles.label}>Extension Type</Typography>

          <Flex gap={isMobile ? 16 : 32} vertical={isMobile}>
            {tenderExtensionType.map((option) => (
              <MRadio
                key={option.codeNum}
                className={styles.radio}
                id={String(option.codeNum)}
                labelText={option.codeDesc}
                {...formik.getFieldProps('extensionType')}
                checked={
                  option.codeDesc ===
                  formik.getFieldProps('extensionType').value
                }
                onChange={() => {
                  formik.setFieldValue('extensionType', option.codeDesc);
                }}
              />
            ))}
          </Flex>
        </Flex>

        <Form.Item label={tenderIdLabel}>
          <MInput
            placeholder={`Insert ${tenderIdLabel}`}
            {...formik.getFieldProps('extensionId')}
          />
        </Form.Item>

        <Form.Item label="Document Type">
          <MUpload
            onFileRequest={handleFileRequest}
            onFileRemove={handleFileRemove}
          />
        </Form.Item>
      </Flex>
    ),
    [
      formik,
      handleFileRemove,
      handleFileRequest,
      isMobile,
      tenderExtensionType,
      tenderIdLabel,
    ]
  );

  return (
    <ViewResolver
      desktop={
        <Modal
          open={isOpen}
          className={styles.modal}
          footer={customButtons}
          width={600}
          title="Tender Extension"
          onCancel={() => {
            onCancel();
            formik.resetForm();
          }}
          destroyOnClose
        >
          {content}
        </Modal>
      }
      mobile={
        <MobileDrawer
          className={styles.drawer}
          title="Tender Extension"
          onClose={onCancel}
          open={isOpen}
          footer={customButtons}
        >
          {content}
        </MobileDrawer>
      }
    />
  );
};

export default TenderExtentionModal;
